import { Button, Card, Container, Row, Col, Form, OverlayTrigger, Tooltip, InputGroup } from 'react-bootstrap';
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import axios from 'axios';
import debounce from 'lodash/debounce';
import config from '../../config';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { v4 as uuidv4 } from 'uuid';
import { alertconfirmation } from '../../alerts/Alerts';
import { carrierOptions } from '../../utils/DropDownOptions';
import LSACoverage from './LSACoverage';

const LSA = forwardRef(({ caseId, renewalCase, isDisableForUsers, initialFormValues = {}, onError = () => { } }, ref) => {

    const [lsas, setLsas] = useState([]);
    const [generalInfo, setGeneralInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const options = [
        { value: '30', label: '30 Days' },
        { value: '60', label: '60 Days' },
        { value: '90', label: '90 Days' },
        { value: '120', label: '120 Days' },
        { value: '0', label: 'Other' },
    ];


    const [expensesArray, setExpensesArray] = useState([]);
    
      
  
      
      // hardcode today's date as 26th Sept 2025
      const today = new Date("2025-09-26T00:00:00");

  // determine createdDate from renewalCase (could be null)
  const createdDate = renewalCase?.createdAt ? new Date(renewalCase.createdAt) : null;

  // Set expensesArray when renewalCase (or createdDate) changes.
  useEffect(() => {
    // determine which expense list to use
    const newExpenses = createdDate && createdDate >= today
      ? [
          "All IRS-Eligible Dental Expenses",
          "All IRS-Eligible Vision Expenses",
          "All IRS-Eligible Medical Expenses",
          "All IRS-Eligible Over-The-Counter (OTC) Items",
          "Deductible",
          "Coinsurance",
          "Copays",
          "Prescriptions",
          "Pediatric Dental/Vision (manual submission only)",
          "In-Network Only",
          "Out-of-Network Only",
          "In- and Out-of-Network ",
        ]
      : [
          "Family Care",
          "Work From Home",
          "Healthy Living",
          "Leisure & Hobbies",
          "Professional Development",
        ];

    // only update if different (prevents unnecessary re-renders)
    setExpensesArray(prev => {
      const same = prev.length === newExpenses.length && prev.every((v,i) => v === newExpenses[i]);
      return same ? prev : newExpenses;
    });
  }, [createdDate]);
      
    

   



  useEffect(() => {
    let cancelled = false;
    const fetchGeneralInfo = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/EBGeneralInformations/ByCaseId/${caseId}`);
        if (!cancelled) setGeneralInfo(response.data || null);
      } catch (err) {
        if (!cancelled) setError('Error fetching plans.');
      }
    };

    const fetchLsa = async () => {
      // don't fetch LSA until expensesArray is set
      if (!caseId || !expensesArray || expensesArray.length === 0) return;

      try {
        const response = await axios.get(`${config.API_URL}/EBLSAPlans/ByCaseId/${caseId}`);
        const lsaData = (response.data || []).map(lsa => {
          
          const right = lsa.lsaExpenses
            ? lsa.lsaExpenses.split(',').map(s => s.trim()).filter(Boolean)
            : [];

          // leftDivisions = expensesArray minus right (compare trimmed strings)
          const left = expensesArray.filter(exp => !right.includes(exp));

          return {
            ...lsa,
            leftDivisions: left,
            rightDivisions: right,
            selectedLeft: [],
            selectedRight: []
          };
        });
        
        if (!cancelled) setLsas(lsaData);
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status !== 404) setError('Error fetching case files.');
          // bubble to parent handler if provided
          if (typeof onError === 'function') onError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    fetchGeneralInfo();
    fetchLsa();

    return () => { cancelled = true; };
  }, [caseId, expensesArray, config.API_URL, onError]);




    const toggleSelection = (division, side, lsaIndex) => {
        setLsas(prevLsas => {
            const updatedLsas = [...prevLsas];
            const selectedLsa = { ...updatedLsas[lsaIndex] };

            if (side === 'left') {
                // Toggle selection for the left side (available expenses)
                if (selectedLsa.selectedLeft.includes(division)) {
                    selectedLsa.selectedLeft = selectedLsa.selectedLeft.filter(item => item !== division);
                } else {
                    selectedLsa.selectedLeft = [...selectedLsa.selectedLeft, division];
                }
            } else if (side === 'right') {
                // Toggle selection for the right side (LSA expenses)
                if (selectedLsa.selectedRight.includes(division)) {
                    selectedLsa.selectedRight = selectedLsa.selectedRight.filter(item => item !== division);
                } else {
                    selectedLsa.selectedRight = [...selectedLsa.selectedRight, division];
                }
            }

            updatedLsas[lsaIndex] = selectedLsa;
            return updatedLsas;
        });
    };





    const moveLeftToRight = (lsaIndex) => {
        setLsas(prevLsas => {
            const updatedLsas = [...prevLsas];
            const selectedLsa = { ...updatedLsas[lsaIndex] };

            // Move selected items from left to right
            const newRightDivisions = [...selectedLsa.rightDivisions, ...selectedLsa.selectedLeft];
            selectedLsa.rightDivisions = newRightDivisions;
            selectedLsa.leftDivisions = selectedLsa.leftDivisions.filter(item => !selectedLsa.selectedLeft.includes(item));

            // Clear selection
            selectedLsa.selectedLeft = [];

            // Update the backend with new LSA expenses
            const patchDocument = [{ op: 'replace', path: '/lsaExpenses', value: newRightDivisions.join(',') }];
            debouncedUpdate(patchDocument, selectedLsa.planId);




            //updatedLsas[lsaIndex] = selectedLsa;
            updatedLsas[lsaIndex] = { ...selectedLsa, ['lsaExpenses']: newRightDivisions.join(',') };
            setLsas(updatedLsas);
            validateField('lsaExpenses', newRightDivisions, lsaIndex);
            return updatedLsas;
        });
    };


    const moveRightToLeft = (lsaIndex) => {
        setLsas(prevLsas => {
            const updatedLsas = [...prevLsas];
            const selectedLsa = { ...updatedLsas[lsaIndex] };

            // Move selected items from right to left
            const newRightDivisions = selectedLsa.rightDivisions.filter(item => !selectedLsa.selectedRight.includes(item));
            selectedLsa.rightDivisions = newRightDivisions;
            selectedLsa.leftDivisions = [...selectedLsa.leftDivisions, ...selectedLsa.selectedRight];

            // Clear selection
            selectedLsa.selectedRight = [];

            // Update the backend with new LSA expenses
            const patchDocument = [{ op: 'replace', path: '/lsaExpenses', value: newRightDivisions.join(',') }];
            debouncedUpdate(patchDocument, selectedLsa.planId);



            //updatedLsas[lsaIndex] = selectedLsa;
            updatedLsas[lsaIndex] = { ...selectedLsa, ['lsaExpenses']: newRightDivisions.join(',') };
            setLsas(updatedLsas);
            validateField('lsaExpenses', newRightDivisions, lsaIndex);
            return updatedLsas;
        });
    };





    const debouncedUpdate = useCallback(
        debounce(async (patchDocument, planId) => {
            try {
                const url = `${config.API_URL}/EBLSAPlans/${planId}`;
                await axios.patch(url, patchDocument);
            } catch (error) {
                console.error('Error updating field:', error);
                onError(error);
            }
        }, 500),
        [onError, validationErrors]
    );

    const validateField = (name, value, index, updateState = true) => {
        let errors = { ...validationErrors };
        let fieldError = null;

        switch (name) {
            case 'planTerminationDate':
                if (lsas[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'Plan termination date is required.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'startDate':
                if (!lsas[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'Plan start date is required.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'planName':
                if (!lsas[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'Plan name is required.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'endDate':
                if (!lsas[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'Plan end date is required.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
                
            case 'maxReimbursementEmbedded':
                if (lsas[index].isEmbedded && !lsas[index].isAutoRenew && !lsas[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'unusedMaxRollAmt':
                if (lsas[index].unusedFundHandling === "RolledOver" && !lsas[index].isAutoRenew && !lsas[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (isNaN(value)) {
                        fieldError = { [name]: 'Amount must be a valid number greater than 0.' };
                    } else if (value > 1000000) {
                        fieldError = { [name]: 'Amount is too large. Please enter a number less than 1,000,000.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }
                break;
            case 'unusedFundPCT':
                if (lsas[index].unusedFundHandling === "RolledOver" && !lsas[index].isAutoRenew && !lsas[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (value !== undefined && (isNaN(value) || value < 0 || value > 100)) {

                        fieldError = { [name]: 'Value must be a percentage between 0 and 100.' };
                    } else {

                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'employeeCostShare':
                if (lsas[index].isEECostSharing && !lsas[index].isAutoRenew && !lsas[index].isTerminating) {
                    if (!value) {
                        fieldError = { [name]: 'This field is required.' };
                    } else if (value !== undefined && (isNaN(value) || value < 0 || value > 100)) {

                        fieldError = { [name]: 'Value must be a percentage between 0 and 100.' };
                    } else {
                        if (errors[index]) delete errors[index][name];
                    }
                }

                break;
            case 'lsaExpenses':
               
                if (!lsas[index].isSameExpenses && !lsas[index].isTerminating && !lsas[index].isAutoRenew && (!value || value.length === 0)) {
                    fieldError = { [name]: 'At least one LSA expense is required.' };
                } else {

                    if (errors[index]) delete errors[index][name];
                }
                break;

            default:
                break;
        }





        if (updateState && fieldError) {
            errors[index] = {
                ...errors[index],
                ...fieldError
            };
            setValidationErrors(errors);
        }

        return fieldError;
    };

    const validateAll = () => {
        let allValid = true;
        let updatedErrors = { ...validationErrors };

        lsas.forEach((lsa, index) => {

            const requiredFields = [
                'planTerminationDate',
                'startDate',
                'endDate',
                'planName',
                'maxReimbursementEmbedded',
                'unusedMaxRollAmt',
                'unusedFundPCT',
                'employeeCostShare',
                'lsaExpenses'
            ];

            requiredFields.forEach(field => {
                const fieldError = validateField(field, lsa[field], index, false);
                if (fieldError) {
                    allValid = false;
                    updatedErrors = {
                        ...updatedErrors,
                        [index]: {
                            ...updatedErrors[index],
                            ...fieldError
                        }
                    };
                }
            });
        });

        setValidationErrors(updatedErrors);
        return allValid;
    };


    useImperativeHandle(ref, () => ({
        validateAll,
    }));


    const handleInputChange = (index, e) => {
        const { name, value } = e.target;
        validateField(name, value, index);

        const updatedLsas = [...lsas];
        updatedLsas[index] = { ...updatedLsas[index], [name]: value };
        setLsas(updatedLsas);

        let patchDocument = [{ op: 'replace', path: `/${name}`, value: value }];

        debouncedUpdate(patchDocument, updatedLsas[index].planId);
    };

    const handleRadioChange = (index, fieldName, e) => {
        const { name, value } = e.target;
        validateField(fieldName, value, index);

        const updatedLsas = [...lsas];
        updatedLsas[index] = { ...updatedLsas[index], [fieldName]: value };
        setLsas(updatedLsas);

        let patchDocument = [{ op: 'replace', path: `/${fieldName}`, value: value }];

        debouncedUpdate(patchDocument, updatedLsas[index].planId);
    };

    const handleSelectChange = (index, name, selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        validateField(name, value, index);

        const updatedLsas = [...lsas];
        updatedLsas[index] = { ...updatedLsas[index], [name]: value };
        setLsas(updatedLsas);

        let patchDocument = [{ op: 'replace', path: `/${name}`, value: value }];

        debouncedUpdate(patchDocument, updatedLsas[index].planId);
    };

    const handleDateChange = (index, fieldName, date) => {
        // If the date is null or undefined (cleared), handle it accordingly
        if (!date) {
            // Clear the field in the state
            const updatedLsas = [...lsas];
            updatedLsas[index] = { ...updatedLsas[index], [fieldName]: '' }; // Clear the date field
            setLsas(updatedLsas);

            // Create the patch document for clearing the date
            let patchDocument = [{ op: 'replace', path: `/${fieldName}`, value: '' }];
            debouncedUpdate(patchDocument, updatedLsas[index].planId);

            return;
        }

        // Normalize the date to prevent timezone issues
        const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        // Format the date as MM/dd/yyyy
        const formattedDate = localDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });

        // Perform field validation
        validateField(fieldName, formattedDate, index);

        // Update state with the selected date
        const updatedLsas = [...lsas];
        updatedLsas[index] = { ...updatedLsas[index], [fieldName]: formattedDate };
        setLsas(updatedLsas);

        // Create the patch document for the selected date
        let patchDocument = [{ op: 'replace', path: `/${fieldName}`, value: formattedDate }];

        // Debounced update to the backend
        debouncedUpdate(patchDocument, updatedLsas[index].planId);
    };

    const handleToggleChange = (
        index,
        e,
        property
    ) => {
    
        const { checked } = e.target;
    
        const value = checked;
    
        const updatedLsas = [...lsas];
    
        const updatedLsa = {
            ...updatedLsas[index]
        };
    
        updatedLsa[property] = value;
    
        let patchDocument = [
            {
                op: 'replace',
                path: `/${property}`,
                value: value
            }
        ];
    
        // Existing logic
        if (
            property === "isPlanChanging" &&
            !checked
        ) {
    
            updatedLsa.isAutoRenew = false;
    
            patchDocument.push({
                op: 'replace',
                path: '/isAutoRenew',
                value: false
            });
        }
    
        // NEW LOGIC FOR TERMINATING
        if (property === "isTerminating") {
    
            // If terminating → auto set date
            if (checked && updatedLsa.startDate) {
    
                const startDate = new Date(
                    updatedLsa.startDate
                );
    
                const terminationDate =
                    new Date(startDate);
    
                terminationDate.setDate(
                    terminationDate.getDate() - 1
                );
    
                updatedLsa.planTerminationDate =
                    terminationDate;
    
                patchDocument.push({
                    op: 'replace',
                    path: '/planTerminationDate',
                    value: terminationDate
                });
            }
    
            // If not terminating → clear date
            if (!checked) {
    
                updatedLsa.planTerminationDate = '';
    
                patchDocument.push({
                    op: 'replace',
                    path: '/planTerminationDate',
                    value: ''
                });
            }
        }
    
        updatedLsas[index] = updatedLsa;
    
        setLsas(updatedLsas);
    
        debouncedUpdate(
            patchDocument,
            updatedLsa.planId
        );
    };

    const handleAddPlan = async () => {
        try {

            const randomId = uuidv4();
            const firststartDate = lsas.length > 0 ? lsas[0].startDate : '';
            const firstendDate = lsas.length > 0 ? lsas[0].endDate : '';

            const newPlan = { ...initialFormValues, planId: caseId+"_"+randomId, startDate: firststartDate, endDate: firstendDate, caseId: caseId, isSameExpenses: false };

            const response = await axios.post(`${config.API_URL}/EBLSAPlans/`, newPlan);

            const addedPlan = {
                ...response.data,
                leftDivisions: expensesArray.filter(expense => !response.data.lsaExpenses?.split(',').includes(expense)) || [],
                rightDivisions: response.data.lsaExpenses ? response.data.lsaExpenses.split(',') : [],
                selectedLeft: [],
                selectedRight: []
            };

            // Update the LSA state with the new plan
            setLsas(prevLsas => [...prevLsas, addedPlan]);

        } catch (error) {
            console.error('Error adding new plan:', error);
            onError(error);
        }
    };

    const handleDeletePlan = async (index) => {
        alertconfirmation(
            `Do you want to delete this specific LSA Plan ?`,
            "Confirmation",
            async () => {
                try {
                    const planId = lsas[index].planId;
                    await axios.delete(`${config.API_URL}/EBLSAPlans/${planId}`);
                    const updatedLsas = lsas.filter((_, i) => i !== index);
                    setLsas(updatedLsas);
                } catch (error) {
                    console.error('Error deleting plan:', error);
                    onError(error);
                }
            }
        );
    };



    const customStyles = (hasError) => ({
        control: (provided) => ({
            ...provided,
            borderColor: hasError ? '#dc3545' : provided.borderColor,
            '&:hover': {
                borderColor: hasError ? '#dc3545' : provided.borderColor,
            },
            boxShadow: hasError ? '0 0 10px rgba(220, 53, 69, 0.5)' : provided.boxShadow,
        }),
    });

    
    
    //const isDisabled = renewalCase?.ebSubmissionStatus === 'Submitted' || isDisableForUsers;
    const isDisabled =  isDisableForUsers;

    if (loading) return <Skeleton />;
    if (error) return <div>{error}</div>;

    return (
        <>
            {generalInfo?.isLSAClient && (<Col lg={12} className='mb-3'>
                <Card>
                    <Card.Body>
                        <h5 className="card-title">SimplyWell Lifestyle Spending Account (LSA)</h5>
                        {lsas.map((lsa, index) => (
                            <Card className='mb-3'>
                                <Card.Body>
                                    <div key={index}>
                                        {lsa.isRenewal && (
                                            <>
                                                <Row className="row">
                                                    <span className="card-title">
                                                        <strong> {`${lsa.planName}`}</strong>
                                                    </span>

                                                </Row>
                                                <Row className='mb-3'>
                                                    <Col lg={4}>
                                                        <div className="d-flex align-items-center">
                                                            <Form.Label>
                                                                Is this LSA Plan terminating?
                                                            </Form.Label>
                                                            <div className="form-check form-switch form-toggle">

                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                    role="switch" value="true"
                                                                    checked={lsa.isTerminating}
                                                                    onChange={(e) => handleToggleChange(index, e, 'isTerminating')}
                                                                    disabled={isDisabled}
                                                                />

                                                            </div>
                                                        </div>

                                                    </Col>


                                                    {lsa.isTerminating && (
                                                        <Col lg={3}>
                                                            <div className="d-flex ">
                                                                <span className="input-group-text">
                                                                    <i className="  far fa-calendar-alt"></i>
                                                                </span>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip id="planTerminationDate-tooltip">Plan Termination Date</Tooltip>}
                                                                >
                                                                    <div>
                                                                        <DatePicker
                                                                            className={`form-control ${validationErrors[index] && validationErrors[index].planTerminationDate ? 'is-invalid' : ''}`}
                                                                            
                                                                            selected={
                                                                                lsa?.planTerminationDate
                                                                                    ? new Date(
                                                                                        lsa.planTerminationDate
                                                                                      )
                                                                                    : null
                                                                            }
                                                                            onChange={(date) => handleDateChange(index, 'planTerminationDate', date)}
                                                                            placeholderText="Plan Termination Date"
                                                                            disabled={isDisabled}
                                                                            minDate={
                                                                                lsa.startDate
                                                                                    ? new Date(
                                                                                          new Date(lsa.startDate).setDate(
                                                                                              new Date(
                                                                                                  lsa.startDate
                                                                                              ).getDate() - 1
                                                                                          )
                                                                                      )
                                                                                    : null
                                                                            }
                                                                        />
                                                                    </div>
                                                                </OverlayTrigger>
                                                            </div>
                                                            {validationErrors[index] && validationErrors[index].planTerminationDate && (
                                                                <div className="invalid-feedback d-block">
                                                                    {validationErrors[index].planTerminationDate}
                                                                </div>
                                                            )}
                                                        </Col>
                                                    )}
                                                </Row>
                                            </>
                                        )}

                                        {!lsa.isTerminating && (
                                            <>
                                                {lsa.isRenewal && (
                                                    <>
                                                    <Row className='mb-3'>
                                                        <Col lg={12}>
                                                            <div className="d-flex align-items-center mb-2">
                                                                <Form.Label>
                                                                Is your LSA plan design staying the same for the new plan year?
                                                                </Form.Label>
                                                                {/* <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Clarity can Auto-Renew the current LSA enrollment for existing participants in the LSA(s). To do so, log into the Administrator site and run the Enrolled Participants Report for the current plan year. Review and verify there are no enrollment/coverage tier changes for participants. if there are no changes, you may select "Yes" to Auto-Renew.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger> */}
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={lsa?.isPlanChanging}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isPlanChanging')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>


                                                            </div>
                                                            
                                                        </Col>


                                                    </Row>
                                                    {lsa?.isPlanChanging && (
                                                    <Row className='mb-3'>
                                                        <Col lg={12}>
                                                            <div className="d-flex align-items-center mb-2">
                                                                <Form.Label>
                                                                    LSA Auto-Renew
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Clarity can Auto-Renew the current LSA enrollment for existing participants in the LSA(s). To do so, log into the Administrator site and run the Enrolled Participants Report for the current plan year. Review and verify there are no enrollment/coverage tier changes for participants. if there are no changes, you may select "Yes" to Auto-Renew.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={lsa.isAutoRenew}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isAutoRenew')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>


                                                            </div>
                                                            {lsa.isAutoRenew && (

                                                                <div className="d-flex  mb-2 ">
                                                                    Yes, we have verified there are no changes to the existing LSA plan design, and the participants will remain the same at the same coverage tier. Please auto-renew participants "as-is" for the new plan year.
                                                                </div>

                                                            )}

                                                        </Col>


                                                    </Row>
                                                    )}
                                                    </>
                                                )}

                                                {!lsa?.isPlanChanging && (
                                                    <>
                                                        

                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Plan Start Date
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="d-flex ">
                                                                    <span className="input-group-text">
                                                                        <i className="  far fa-calendar-alt"></i>
                                                                    </span>
                                                                    <DatePicker
    className={`form-control ${validationErrors[index] && validationErrors[index].startDate ? 'is-invalid' : ''}`}
    selected={
        lsa?.startDate
            ? new Date(lsa.startDate)
            : null
    }
    onChange={(date) => handleDateChange(index, 'startDate', date)}
    placeholderText="Plan Start Date"
    disabled={isDisabled}
/>
                                                                </div>
                                                                {validationErrors[index] && validationErrors[index].startDate && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {validationErrors[index].startDate}
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Plan End Date
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="d-flex ">
                                                                    <span className="input-group-text">
                                                                        <i className="  far fa-calendar-alt"></i>
                                                                    </span>

                                                                    <DatePicker
    className={`form-control ${validationErrors[index] && validationErrors[index].endDate ? 'is-invalid' : ''}`}
    selected={
        lsa?.endDate
            ? new Date(lsa.endDate)
            : null
    }
    onChange={(date) => handleDateChange(index, 'endDate', date)}
    placeholderText="Plan End Date"
    minDate={lsa.startDate ? new Date(lsa.startDate) : null}
    disabled={isDisabled}
/>
                                                                </div>
                                                                {validationErrors[index] && validationErrors[index].endDate && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {validationErrors[index].endDate}
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Claims Runout Period for Active Employees
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <div className="d-flex ">
                                                                    <span className="input-group-text">
                                                                        <i className="  far fa-calendar-alt"></i>
                                                                    </span>

                                                                    <DatePicker
                                                                        className={`form-control ${validationErrors[index] && validationErrors[index].lsaClaimsRunoutActiveEmp ? 'is-invalid' : ''}`}
                                                                        selected={lsa.lsaClaimsRunoutActiveEmp || ''}
                                                                        onChange={(date) => handleDateChange(index, 'lsaClaimsRunoutActiveEmp', date)}
                                                                        placeholderText="Please select"
                                                                        minDate={new Date()}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>

                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-5'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Claims Runout Period for Terminated Employees
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3}>

                                                                <Select
                                                                    className={`flex-grow-1 `}
                                                                    name="lsaClaimsRunoutTerminatedEmp"
                                                                    options={options}
                                                                    value={options?.find(option => option.value === lsa.lsaClaimsRunoutTerminatedEmp)}
                                                                    placeholder="Please select"
                                                                    onChange={(selectedOption) => handleSelectChange(index, 'lsaClaimsRunoutTerminatedEmp', selectedOption)}
                                                                    styles={customStyles(validationErrors[index] && validationErrors[index].lsaClaimsRunoutTerminatedEmp)}
                                                                    isDisabled={isDisabled}
                                                                />
                                                            </Col>
                                                        </Row>

                                                        <Row className='mb-3'>
                                                        <Col lg={12}>
                            <div className="d-flex align-items-center">
                                <Form.Label>
                                Will the LSA reimburse same expenses as current plan year?
                                </Form.Label>
                                <div className="form-check form-switch form-toggle">
                                    <input
                                        type="checkbox"
                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                        role="switch"
                                        checked={lsa.isSameExpenses}
                                        onChange={(e) => handleToggleChange(index, e, 'isSameExpenses')}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>
                        </Col>
                                                        </Row>

                                                        {!lsa.isSameExpenses && (
                                                            <>
 <Row>
                                                            <Col lg={12}>
                                                                <Form.Label>
                                                                    Please select all expenses reimbursable through this LSA
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            In-network/out-of-network cannot be verified via Clarity Benefit Card and/or certain insurance provider&#x27;s EOBs.                                           </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                        </Row>

                                                        <Row className="row mb-5 divisionsContainer">
                                                            <Col lg={12} className="expensesContainer container">
                                                                {/* Left Block: Available Expenses */}
                                                                <div className='block-container'>
                                                                    <label className='ms-2'><strong>Available</strong></label>
                                                                    <div className="block left-block">
                                                                        {lsa.leftDivisions.map((division, idx) => (
                                                                            <p
                                                                                key={idx}
                                                                                data-index={division}
                                                                                onClick={() => toggleSelection(division, 'left', index)}  // Handle selection
                                                                                className={lsa.selectedLeft.includes(division) ? 'selected' : ''}
                                                                            >
                                                                                {division}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                </div>


                                                                {/* Arrow Buttons to Move Items */}
                                                                <div className="arrow-container">
                                                                    <i
                                                                        className="arrow fas fa-arrow-right right-arrow"
                                                                        onClick={() => moveLeftToRight(index)} disabled={isDisabled}
                                                                    ></i>
                                                                    <i
                                                                        className="arrow fas fa-arrow-left left-arrow"
                                                                        onClick={() => moveRightToLeft(index)} disabled={isDisabled}
                                                                    ></i>
                                                                </div>

                                                                {/* Right Block: LSA Selected Expenses */}
                                                                <div className='block-container'>
                                                                    <label className='ms-3'><strong>Selected</strong></label>
                                                                    <div
                                                                        className="block right-block"
                                                                        style={
                                                                            validationErrors[index] && validationErrors[index].lsaExpenses
                                                                                ? {
                                                                                    paddingRight: 'calc(1.5em + 0.75rem)',
                                                                                    boxShadow: 'rgba(220, 53, 69, 0.5) 0px 0px 10px',
                                                                                    borderColor: '#dc3545'
                                                                                }
                                                                                : {}
                                                                        }
                                                                    >
                                                                        {lsa.rightDivisions.map((division, idx) => (
                                                                            <p
                                                                                key={idx}
                                                                                data-index={division}
                                                                                onClick={() => toggleSelection(division, 'right', index)} // Handle selection
                                                                                className={lsa.selectedRight.includes(division) ? 'selected' : ''}
                                                                            >
                                                                                {division}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                    {validationErrors[index] && validationErrors[index].lsaExpenses && (
                                                                        <div className="invalid-feedback d-block">
                                                                            {validationErrors[index].lsaExpenses}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                            </Col>
                                                        </Row>
                                                            </>
                                                        )}

<Row className='mb-3'>
                                                                    <Col lg={12}>
                                                                   <strong>Funds availabilty</strong> 
                                                                    </Col>
                                                                </Row>
                                                                <Row className='mb-3'>
                                                                    <Col lg={12}>
                                                                    <div className="form-check-inline">
                                                                    <Form.Label>
                                                                    <strong>Funds will be made available to participants:</strong>
                                                                </Form.Label>
                                                                    </div>
                                                                    
                                                                    

                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="Monthly"
                                                                                name={`FundsAvailability_${index}`}
                                                                                id={`FundsAvailability_Monthly_${index}`}
                                                                                checked={lsa?.fundsAvailability === "Monthly"}
                                                                                onChange={(e) => handleRadioChange(index, 'fundsAvailability', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`FundsAvailability_Monthly_${index}`}>Monthly</label>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`tooltip-plan-rule-Monthly`}>
                                                                                        Contributions will be automatically posted monthly on the first day of each month to participant accounts.
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="btn btn-sm btn-bg-soft-secondary ">
                                                                                    <i className="dropicons dripicons-information"></i>
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </div>

                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="Quarterly"
                                                                                name={`FundsAvailability_${index}`}
                                                                                id={`FundsAvailability_Quarterly_${index}`}
                                                                                checked={lsa?.fundsAvailability === "Quarterly"}
                                                                                onChange={(e) => handleRadioChange(index, 'fundsAvailability', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`FundsAvailability_Quarterly_${index}`}>Quarterly</label>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`tooltip-plan-rule-Quarterly`}>
                                                                                        Contributions will be automatically posted quarterly on the first day of each quarter to participant accounts.
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="btn btn-sm btn-bg-soft-secondary ">
                                                                                    <i className="dropicons dripicons-information"></i>
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </div>

                                                                        <div className="form-check-inline">
                                                                            <input
                                                                                type="radio"
                                                                                className="emp-form-check-input"
                                                                                value="Annually"
                                                                                name={`FundsAvailability_${index}`}
                                                                                id={`FundsAvailability_Annually_${index}`}
                                                                                checked={lsa?.fundsAvailability === "Annually"}
                                                                                onChange={(e) => handleRadioChange(index, 'fundsAvailability', e)}
                                                                                disabled={isDisabled}
                                                                            />
                                                                            <label htmlFor={`FundsAvailability_Annually_${index}`}>Annually</label>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id={`tooltip-plan-rule-Annually`}>
                                                                                        Funds are available in full to participants on the first day of the plan year.
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span className="btn btn-sm btn-bg-soft-secondary ">
                                                                                    <i className="dropicons dripicons-information"></i>
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </div>

                                                                        

                                                                        
                                                                    </Col>
                                                                </Row>


                                                       



                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Carrier Name
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Pending with tooltip information
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                            <Col lg={3}>

                                                                <Select
                                                                    className={`flex-grow-1 `}
                                                                    name="carrierName"
                                                                    options={carrierOptions}
                                                                    value={carrierOptions?.find(option => option.value === lsa.carrierName)}
                                                                    placeholder="Please select"
                                                                    onChange={(selectedOption) => handleSelectChange(index, 'carrierName', selectedOption)}
                                                                    styles={customStyles(validationErrors[index] && validationErrors[index].carrierName)}
                                                                    isDisabled={isDisabled}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-3'>
                                                            <Col lg={3}>
                                                                <Form.Label>
                                                                    Plan Name
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            Pending with tooltip information
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                            <Col lg={3}>
                                                                <Form.Control
                                                                    className={`form-control  ${validationErrors[index] && validationErrors[index].planName ? 'is-invalid' : ''} `}
                                                                    title="Plan name"
                                                                    type="text"
                                                                    name="planName"
                                                                    placeholder="Plan name"
                                                                    value={lsa.planName || ''}
                                                                    onChange={(e) => handleInputChange(index, e)}
                                                                    disabled={isDisabled}
                                                                />
                                                                {validationErrors[index] && validationErrors[index].planName && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {validationErrors[index].planName}
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}
                                                <Row className='mb-3'>
                                                    <Col lg={3}>
                                                        <Form.Label>
                                                            LSA Funding
                                                        </Form.Label>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip id={`tooltip-plan-rule-`}>
                                                                    Please enter the annual amount the company is funding for each tier.
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                <i className="dropicons dripicons-information"></i>
                                                            </span>
                                                        </OverlayTrigger>
                                                    </Col>
                                                </Row>

                                                <Row >
                        <Col lg={12}>
                            <LSACoverage
                                lsaId={lsa.planId}
                                renewalCase={renewalCase}
                                isDisableForUsers={isDisableForUsers}
                                initialFormValues={{}} />
                        </Col>
                    </Row>

                                                

                                                {!lsa?.isPlanChanging && (
                                                    <>
                                                        <Row className='mb-3'>
                                                            <Col lg={6}>
                                                                <Form.Label>
                                                                    Should the LSA contribution be pro-rated for mid-year enrollments?
                                                                </Form.Label>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`tooltip-plan-rule-`}>
                                                                            The system will not automatically calculate the pro-rated amount, if allowed. This will need to be provided upon enrollment.
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                        <i className="dropicons dripicons-information"></i>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                            <Col lg={6} className="d-flex align-items-start">
                                                                <div className="form-check form-switch form-toggle">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                                        role="switch"
                                                                        checked={lsa.isProRatedMYE}
                                                                        onChange={(e) => handleToggleChange(index, e, 'isProRatedMYE')}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        
                                                        

                                                        
                                                        

                                                        
                                                        
                                                        <Row className='mb-3'>
                                                            <Col lg={5}>
                                                                <Form.Label>
                                                                    How will unused funds remaining in the LSA be handled?
                                                                </Form.Label>
                                                            </Col>
                                                            <Col lg={3} className="d-flex flex-column">
                                                                <fieldset>
                                                                    <div className="form-check-inline mb-2">
                                                                        <input
                                                                            type="radio"
                                                                            className="emp-form-check-input"
                                                                            value="Forfeited"
                                                                            name={`unusedFundHandling_${index}`}
                                                                            id={`unusedFundHandling_Forfeited_${index}`}
                                                                            checked={lsa?.unusedFundHandling === "Forfeited"}
                                                                            onChange={(e) => handleRadioChange(index, 'unusedFundHandling', e)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <label htmlFor={`unusedFundHandling_Forfeited_${index}`} className='ms-2'>Forfeited</label>
                                                                    </div>
                                                                    <div className="form-check-inline">
                                                                        <input
                                                                            type="radio"
                                                                            className="emp-form-check-input"
                                                                            value="RolledOver"
                                                                            name={`unusedFundHandling_${index}`}
                                                                            id={`unusedFundHandling_RolledOver_${index}`}
                                                                            checked={lsa?.unusedFundHandling === "RolledOver"}
                                                                            onChange={(e) => handleRadioChange(index, 'unusedFundHandling', e)}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <label htmlFor={`unusedFundHandling_RolledOver_${index}`} className='ms-2'>Rolled over the next year plan</label>
                                                                    </div>
                                                                </fieldset>
                                                            </Col>
                                                            {lsa?.unusedFundHandling === "RolledOver" && (
                                                                <>
                                                                    <Col lg={2}>
                                                                        <InputGroup>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip id="tooltip-unusedMaxRollAmt">Maximum Rollover Amount($)</Tooltip>}
                                                                            >
                                                                                <Form.Control
                                                                                    className={`form-control ${validationErrors[index] && validationErrors[index].unusedMaxRollAmt ? 'is-invalid' : ''}`}
                                                                                    title="unusedMaxRollAmt"
                                                                                    type="text"
                                                                                    name={`unusedMaxRollAmt_${index}`}
                                                                                    placeholder="Maximum Rollover Amount($)"
                                                                                    value={lsa.unusedMaxRollAmt || ''}
                                                                                    onChange={(e) => handleRadioChange(index, 'unusedMaxRollAmt', e)}
                                                                                    disabled={isDisabled}
                                                                                />
                                                                            </OverlayTrigger>
                                                                            {validationErrors[index] && validationErrors[index].unusedMaxRollAmt && (
                                                                                <div className="invalid-feedback d-block">
                                                                                    {validationErrors[index].unusedMaxRollAmt}
                                                                                </div>
                                                                            )}
                                                                        </InputGroup>
                                                                    </Col>
                                                                    <Col lg={2}>
                                                                        <InputGroup>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip id="tooltip-unusedFundPCT">Percentage of Unused Funds(%)</Tooltip>}
                                                                            >
                                                                                <Form.Control
                                                                                    className={`form-control ${validationErrors[index] && validationErrors[index].unusedFundPCT ? 'is-invalid' : ''}`}
                                                                                    title="unusedFundPCT"
                                                                                    type="text"
                                                                                    name={`unusedFundPCT_${index}`}
                                                                                    placeholder="Percentage of Unused Funds(%)"
                                                                                    value={lsa.unusedFundPCT || ''}
                                                                                    onChange={(e) => handleRadioChange(index, 'unusedFundPCT', e)}
                                                                                    disabled={isDisabled}
                                                                                />
                                                                            </OverlayTrigger>
                                                                            {validationErrors[index] && validationErrors[index].unusedFundPCT && (
                                                                                <div className="invalid-feedback d-block">
                                                                                    {validationErrors[index].unusedFundPCT}
                                                                                </div>
                                                                            )}
                                                                        </InputGroup>
                                                                    </Col>
                                                                </>
                                                            )}
                                                        </Row>

                                                        
                                                       

                                                        


                                                       
                                                    </>
                                                )}

                                            </>
                                        )}
                                    </div>
                                </Card.Body>
                                <Card.Footer >
                                    <Row className='mb-3'>
                                        <Col lg={8} className='d-flex align-items-center'>

                                        </Col>
                                        {!lsa.isRenewal && (
                                            <Col lg={4}>

                                                <button className="delete-plan-info" onClick={() => handleDeletePlan(index)} disabled={isDisabled}>
                                                    <span className="fa fa-trash-alt me-1" id="delete"></span>
                                                </button>
                                            </Col>
                                        )}

                                    </Row>
                                </Card.Footer>
                            </Card>
                        ))}



                    </Card.Body>
                    <Card.Footer >
                        <Row className='mb-3'>
                            <Col lg={8} className='d-flex align-items-center'>
                                <Form.Label className='mb-0 me-3'>
                                Do you have another LSA plan to add?
                                </Form.Label>
                                <Button
                                    className="btn btn-success btn-sm"
                                    onClick={handleAddPlan} disabled={isDisabled}
                                >
                                    ADD LSA
                                </Button>
                            </Col>
                            <Col lg={4}>

                                {/* <Button className="delete-plan-info" onClick={() => handleDeletePlan(index)} disabled={isDisabled} >
                                                                    <span className="fa fa-trash-alt me-1" id="delete"></span>
                                                                </Button> */}
                            </Col>
                        </Row>
                    </Card.Footer>
                </Card>
            </Col>)}
        </>


    );
});

export default LSA;